import type { LucideIcon } from 'lucide-react';
import {
    Activity, Bell, Building2, CalendarDays, Clock, Cpu, DoorOpen, Factory, Gauge, Globe, Hash, Home, Hourglass,
    Landmark, Layers, Link, Mailbox, MapPin, Nfc, Package, RadioTower, Ruler, Signpost, SlidersHorizontal, Tag,
} from 'lucide-react';

import { alertSampleContext, ALERT_PLACEHOLDER_NAMES, placeholderTag, renderAlertTemplate } from './alertPlaceholders';

/**
 * What the "Insert placeholder" picker offers.
 *
 * Presentation only. Which tokens are valid is decided by `ALERT_PLACEHOLDERS` /
 * `ACCEPTED_PLACEHOLDER_NAMES` (mirror of the server); this list is a curated subset of those, so
 * the picker never offers a name the server does not resolve. Everything left out here stays valid
 * when typed or when it is already in a saved rule:
 *
 *  - `_id`, `assetObjectId`, `asset.id`, `asset.name`, `asset.address`, `asset.floor`,
 *    `asset.space`, `asset` — legacy duplicates of an entry below (`id`, `name`, `location`,
 *    `floor`, `space`);
 *  - `metric`, `condition` — the raw keys a rule is written against; `metricLabel` and
 *    `conditionLabel` say the same in words.
 */
export type PickerGroupId = 'device' | 'location' | 'reading' | 'rule' | 'uplink' | 'link';

export interface PickerEntry {
    /** The name between the braces, e.g. `asset.product`. */
    name: string;
    group: PickerGroupId;
    /** i18n key below `picker.items.` (label and hint live there). */
    key: string;
}

/** Display order of the groups. */
export const PICKER_GROUPS: PickerGroupId[] = ['device', 'location', 'reading', 'rule', 'uplink', 'link'];

/**
 * One icon per label key — the single place that decides how a placeholder looks (picker and badges).
 * Decorative only; the accessible name is always the label.
 */
export const PLACEHOLDER_ICONS: Record<string, LucideIcon> = {
    id: Hash, name: Tag, product: Package, deviceId: Nfc,
    building: Building2, floor: Layers, space: DoorOpen, location: MapPin, street: Signpost,
    streetNumber: Home, postalCode: Mailbox, city: Landmark, country: Globe,
    metricLabel: Activity, value: Gauge, unit: Ruler, threshold: SlidersHorizontal, conditionLabel: SlidersHorizontal,
    ruleName: Bell, date: CalendarDays, time: Clock, timestamp: Clock,
    lastUplink: RadioTower, daysSilent: Hourglass, stage: Factory, link: Link,
    objectId: Hash, metric: Activity, condition: SlidersHorizontal, asset: Tag,
};

export const PICKER_GROUP_ICONS: Record<PickerGroupId, LucideIcon> = {
    device: Cpu, location: MapPin, reading: Gauge, rule: Bell, uplink: RadioTower, link: Link,
};

export const PICKER_ENTRIES: PickerEntry[] = [
    { name: 'id', group: 'device', key: 'id' },
    { name: 'name', group: 'device', key: 'name' },
    { name: 'asset.product', group: 'device', key: 'product' },
    { name: 'deviceId', group: 'device', key: 'deviceId' },

    { name: 'asset.building', group: 'location', key: 'building' },
    { name: 'floor', group: 'location', key: 'floor' },
    { name: 'space', group: 'location', key: 'space' },
    { name: 'location', group: 'location', key: 'location' },
    { name: 'street', group: 'location', key: 'street' },
    { name: 'streetNumber', group: 'location', key: 'streetNumber' },
    { name: 'postalCode', group: 'location', key: 'postalCode' },
    { name: 'city', group: 'location', key: 'city' },
    { name: 'country', group: 'location', key: 'country' },

    { name: 'metricLabel', group: 'reading', key: 'metricLabel' },
    { name: 'value', group: 'reading', key: 'value' },
    { name: 'unit', group: 'reading', key: 'unit' },
    { name: 'threshold', group: 'reading', key: 'threshold' },
    { name: 'conditionLabel', group: 'reading', key: 'conditionLabel' },

    { name: 'ruleName', group: 'rule', key: 'ruleName' },
    { name: 'date', group: 'rule', key: 'date' },
    { name: 'time', group: 'rule', key: 'time' },
    { name: 'timestamp', group: 'rule', key: 'timestamp' },

    { name: 'lastUplink', group: 'uplink', key: 'lastUplink' },
    { name: 'daysSilent', group: 'uplink', key: 'daysSilent' },
    { name: 'stage', group: 'uplink', key: 'stage' },

    { name: 'link', group: 'link', key: 'link' },
];

/** The entries of one picker: the uplink group only exists for a "days without uplink" rule. */
export const pickerEntriesFor = (options: { noUplink: boolean }): PickerEntry[] =>
    PICKER_ENTRIES.filter((entry) => options.noUplink || entry.group !== 'uplink');

/** The sample value the preview shows for a token — one source, so picker and preview agree. */
export const pickerExample = (name: string, language?: string): string =>
    renderAlertTemplate(placeholderTag(name), alertSampleContext(language));

/** Every picker entry must be a name the server resolves; used by the tests. */
export const pickerNamesAreAccepted = (): boolean =>
    PICKER_ENTRIES.every((entry) => ALERT_PLACEHOLDER_NAMES.includes(entry.name));

/**
 * The picker entry (label key) a token that is not offered is shown as.
 *
 * Legacy duplicates read like their current twin (`asset.id` is the device number), the rest get a
 * neutral label of their own. `null` means the name is not a token the server resolves.
 */
const LEGACY_LABEL_KEYS: Record<string, string> = {
    _id: 'objectId',
    assetObjectId: 'objectId',
    'asset.id': 'id',
    'asset.name': 'name',
    'asset.address': 'location',
    'asset.floor': 'floor',
    'asset.space': 'space',
    asset: 'asset',
    metric: 'metric',
    condition: 'condition',
};

export const pickerLabelKeyOf = (name: string): { key: string; legacy: boolean } | null => {
    const offered = PICKER_ENTRIES.find((entry) => entry.name === name);

    if (offered) {
        return { key: offered.key, legacy: false };
    }

    return LEGACY_LABEL_KEYS[name] ? { key: LEGACY_LABEL_KEYS[name], legacy: true } : null;
};
